import { useTheme } from "@emotion/react";
import {
  Children,
  useContext,
  // useContext,
  useMemo,
} from "react";

import pageGridContext from "./pageGridContext";
import useBodyWidth from "./useBodyWidth";
import useComponentWidth from "./useComponentWidth";

export default function usePageGrid(props) {
  let {
    children,
    maxWidth: contentMaxWidth = 304,
    spacing = [4, 8],
    gap = spacing,
    maxColspan: defaultMaxColspan = 8,
    minColspan: defaultMinColspan = 3,
    items,
    columnCount,
    ...restProps
  } = props;
  const parentContext = useContext(pageGridContext);
  const theme = useTheme();
  let {
    minViewport = 320,
    maxViewport = 1280,
    // decimals = 4,
  } = theme?.layout || {};
  const windowWidthPx = useBodyWidth();
  const [componentWidthPx, ref] = useComponentWidth();
  const fluidLength = (min, max) => {
    if (windowWidthPx <= minViewport) {
      return min;
    }
    if (windowWidthPx >= maxViewport) {
      return max;
    }
    return (
      min +
      (max - min) *
        ((windowWidthPx - minViewport) / (maxViewport - minViewport))
    );
  };

  const isSubgrid = !!parentContext?.item;

  let gapMinWidthPx;
  let gapMaxWidthPx;
  let gapWidthPx;

  let paddingMinWidthPx;
  let paddingMaxWidthPx;
  let paddingWidthPx;

  let gridMinWidthPx;
  let gridMaxWidthPx;
  let gridWidthPx;

  let columnMinWidthPx;
  let columnMaxWidthPx;
  let columnWidthPx;

  let contentWidthPx;

  if (isSubgrid) {
    columnCount = columnCount ?? parentContext.item.itemColumnCount;

    gapMinWidthPx = parentContext.grid.gapMinWidthPx;
    gapMaxWidthPx = parentContext.grid.gapMaxWidthPx;
    gapWidthPx = parentContext.grid.gapWidthPx;

    paddingMinWidthPx = parentContext.grid.paddingMinWidthPx;
    paddingMaxWidthPx = parentContext.grid.paddingMaxWidthPx;
    paddingWidthPx = parentContext.grid.paddingWidthPx;

    gridMinWidthPx = parentContext.item.itemMinWidthPx + gapMinWidthPx;
    gridMaxWidthPx = parentContext.item.itemMaxWidthPx + gapMaxWidthPx;
    gridWidthPx = Math.max(
      gridMinWidthPx,
      Math.min(componentWidthPx, gridMaxWidthPx),
    );

    columnMinWidthPx = parentContext.grid.columnMinWidthPx;
    columnMaxWidthPx = parentContext.grid.columnMaxWidthPx;
    columnWidthPx = parentContext.grid.columnWidthPx;

    contentWidthPx = gridWidthPx - gapWidthPx;
  } else {
    let contentMaxWidthPx;
    let contentMinWidthPx;

    columnCount = columnCount ?? 12;

    gapMinWidthPx = theme.getLengthPx(gap[0]);
    gapMaxWidthPx = theme.getLengthPx(gap[1]);
    gapWidthPx = fluidLength(gapMinWidthPx, gapMaxWidthPx);

    paddingMinWidthPx = theme.getLengthPx(spacing[0]);
    paddingMaxWidthPx = theme.getLengthPx(spacing[1]);
    paddingWidthPx = fluidLength(paddingMinWidthPx, paddingMaxWidthPx);

    contentMaxWidthPx = theme.getLengthPx(contentMaxWidth);
    contentMinWidthPx = minViewport - paddingMinWidthPx * 2;

    gridMinWidthPx = contentMinWidthPx + gapMinWidthPx;
    gridMaxWidthPx = contentMaxWidthPx + gapMaxWidthPx;
    gridWidthPx = Math.max(
      gridMinWidthPx,
      Math.min(windowWidthPx - paddingWidthPx * 2 + gapWidthPx, gridMaxWidthPx),
    );

    columnMinWidthPx = gridMinWidthPx / columnCount;
    columnMaxWidthPx = gridMaxWidthPx / columnCount;
    columnWidthPx = gridWidthPx / columnCount;

    contentWidthPx = gridWidthPx - gapWidthPx;
  }

  children = Children.toArray(children);
  if (children.length && items) {
    throw new Error("PageGrid: cannot have both children and items");
  }
  items = useMemo(
    () =>
      (items =
        items ||
        children.map(({ props: { ...props } }) => {
          return { ...props };
        })),
    [!items, children],
  );
  items = items?.map((item) => {
    let { colspan = [3], maxWidth = "none" } = item;
    if (!Array.isArray(colspan)) {
      colspan = [colspan];
    }
    if (colspan.length === 1) {
      colspan = [colspan[0], Math.max(defaultMaxColspan, colspan[0])];
    }
    if (colspan.length === 2) {
      colspan = [Math.min(defaultMinColspan, colspan[0]), ...colspan];
    }
    let [minColspan, baseColspan, maxColspan] = colspan;
    let itemMinWidthPx = minColspan * columnMaxWidthPx - gapMaxWidthPx;
    let itemMaxWidthPx = maxColspan * columnMaxWidthPx - gapMaxWidthPx;
    let itemBaseWidthPx = baseColspan * columnMaxWidthPx - gapMaxWidthPx;
    let itemFlexGrow = itemBaseWidthPx - itemMinWidthPx + 1;
    let itemFlexShrink = itemBaseWidthPx - itemMinWidthPx + 1;
    let itemColumnCount = baseColspan;
    return {
      ...item,
      colspan,
      maxWidth,
      itemMinWidthPx,
      itemMaxWidthPx,
      itemBaseWidthPx,
      itemFlexGrow,
      itemFlexShrink,
      itemColumnCount,
    };
  });
  const context = {
    grid: {
      columnCount,
      windowWidthPx,
      gapMinWidthPx,
      gapMaxWidthPx,
      gapWidthPx,
      // paddingMinWidthPx,
      // paddingMaxWidthPx,
      // paddingWidthPx,
      gridMinWidthPx,
      gridMaxWidthPx,
      gridWidthPx,
      columnMinWidthPx,
      columnMaxWidthPx,
      columnWidthPx,
      contentWidthPx,
      items,
    },
  };
  return {
    ...restProps,
    // PageGridProvider: ({ children }) => (
    //   <pageGridContext.Provider value={context}>
    //     {children}
    //   </pageGridContext.Provider>
    // ),
    context,
    ref,
    isSubgrid,
    items,
  };
}
